import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { isFailedQuote, isOutOfHoursQuote, QUOTE_NO_ACCESS, type DeliveryQuote } from '@/lib/delivery-providers-client';
import { formatKm } from './delivery-form';

interface DeliveryCoverageLineProps {
  providerName: string;
  /** The coverage answer (a quote of a provider without `QUOTE`: `fee` is always null). */
  quote: DeliveryQuote | undefined;
  /** The answer is on its way. */
  pending: boolean;
  /** The read itself failed: what to say about it. */
  errorText?: string | null;
  /** Nothing can be asked yet (the address is not on the map): what to say instead. */
  idle?: ReactNode;
  /**
   * The booking is held by Menteo for later: an out-of-hours answer then only warns (the booking job
   * checks coverage again when the hold ends) instead of blocking.
   */
  heldForLater?: boolean;
}

/** "Dentro de la cobertura de <provider> · 2,4 km (hasta 3 km)" — the covered line, in the UI language. */
export function useCoveredText() {
  const { t, i18n } = useTranslation('orders');
  return (providerName: string, quote: DeliveryQuote): string => {
    if (quote.distanceKm == null) return t('delivery.newDelivery.coveredNoDistance', { provider: providerName });
    const distance = formatKm(quote.distanceKm, i18n.language);
    return quote.maxDistanceKm != null
      ? t('delivery.newDelivery.coveredMax', {
          provider: providerName,
          distance,
          max: formatKm(quote.maxDistanceKm, i18n.language),
        })
      : t('delivery.newDelivery.covered', { provider: providerName, distance });
  };
}

/**
 * Where the rider dialogs show "Costo <provider>" for a provider that prices, a provider that only
 * answers coverage (Logística 21) shows whether it takes the delivery — never a money figure. Out of
 * zone is red (the dialogs refuse to book on it); out of hours is red when the request goes out now
 * and a warning when Menteo holds it for later; any other failure is a warning.
 */
export function DeliveryCoverageLine({
  providerName,
  quote,
  pending,
  errorText,
  idle,
  heldForLater = false,
}: DeliveryCoverageLineProps) {
  const { t } = useTranslation('orders');
  const coveredText = useCoveredText();

  let content: ReactNode = null;
  if (idle) {
    content = <span className="text-xs text-muted-foreground">{idle}</span>;
  } else if (pending) {
    content = (
      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {t('delivery.newDelivery.checkingCoverage', { provider: providerName })}
      </span>
    );
  } else if (errorText) {
    content = <span className="text-xs text-amber-700 dark:text-amber-300">{errorText}</span>;
  } else if (isFailedQuote(quote)) {
    content = (
      <span className="text-xs text-amber-700 dark:text-amber-300">
        {quote?.reason === QUOTE_NO_ACCESS
          ? t('delivery.newDelivery.coverageNoAccess', { provider: providerName })
          : t('delivery.newDelivery.coverageUnavailable', { provider: providerName })}
      </span>
    );
  } else if (quote && !quote.covered) {
    // Out of hours says "not now", never "does not reach"; held for later it is only a warning.
    const outOfHours = isOutOfHoursQuote(quote);
    const warnOnly = outOfHours && heldForLater;
    content = (
      <span
        className={
          warnOnly
            ? 'block text-xs font-medium text-amber-700 dark:text-amber-300'
            : 'block text-xs font-medium text-red-700 dark:text-red-300'
        }
      >
        <span className="inline-flex items-center gap-1">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {outOfHours
            ? t('delivery.newDelivery.notAcceptingNow', { provider: providerName })
            : t('delivery.newDelivery.notCovered', { provider: providerName })}
        </span>
        {quote.detail && (
          <span className="mt-0.5 block text-[11px] font-normal break-words text-muted-foreground">{quote.detail}</span>
        )}
        {warnOnly && (
          <span className="mt-0.5 block text-[11px] font-normal text-muted-foreground">
            {t('delivery.newDelivery.outOfHoursHeld', { provider: providerName })}
          </span>
        )}
      </span>
    );
  } else if (quote?.covered) {
    content = (
      <span className="inline-flex items-start gap-1.5 text-foreground">
        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
        {coveredText(providerName, quote)}
      </span>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-muted/20 px-3 py-2.5 text-sm" aria-live="polite">
      {content ?? <span className="text-xs text-muted-foreground">—</span>}
    </div>
  );
}
